// TasksPanel.jsx

import { useState } from "react";
import { Plus, Trash2, Edit2 } from "lucide-react";

import { useTasksData } from "@/hooks/useTasksData";

export default function TasksPanel() {
  const { tasks, add, toggleCompleted, saveEdit: updateTask, remove } = useTasksData();
  const [isAdding, setIsAdding] = useState(false);
  const [newText, setNewText] = useState("");
  const [newPriority, setNewPriority] = useState("LOW");

  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({ text: "", priority: "LOW" });

  function toggleAdd() {
    setIsAdding((v) => !v);
    setNewText("");
    setNewPriority("LOW");
  }

  function addTask() {
    if (!newText.trim()) return;

    add(newText, newPriority);
    toggleAdd();
  }

  function handleAddKey(e) {
    if (e.key === "Enter") addTask();
    if (e.key === "Escape") toggleAdd();
  }

  function toggleCompletedLocal(id) {
    toggleCompleted(id);
  }

  function startEdit(task) {
    setEditingId(task.id);
    setEditDraft({ text: task.text, priority: task.priority });
  }

  function cancelEdit() {
    setEditingId(null);
    setEditDraft({ text: "", priority: "LOW" });
  }

function saveEditLocal(id) {
    updateTask(id, editDraft);
    cancelEdit();
  }

  function deleteTask(id) {
    remove(id);
  }

  function handleEditKey(e, id) {
if (e.key === "Enter") saveEditLocal(id);
    if (e.key === "Escape") cancelEdit();
  }

  return (
    <div className="console-card p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="uppercase text-sm tracking-wider">Tasks</span>
          <span className="console-badge">{tasks.length}</span>
        </div>

        <button
          onClick={toggleAdd}
          className="w-9 h-9 rounded-full border border-cyan-400/40 bg-black/40 flex items-center justify-center"
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>

      {/* Add */}
      {isAdding && (
        <div className="mb-4 space-y-2">
          <input
            className="w-full px-4 py-2 rounded bg-black/40 border border-white/10"
            placeholder="New task..."
            value={newText}
            onChange={(e) => setNewText(e.target.value)}
            onKeyDown={handleAddKey}
            autoFocus
          />

          <div className="flex gap-2">
            <select
              className="px-3 py-2 rounded-full text-xs bg-black/60 border border-white/20"
              value={newPriority}
              onChange={(e) => setNewPriority(e.target.value)}
            >
              <option value="LOW">low</option>
              <option value="MEDIUM">medium</option>
              <option value="HIGH">high</option>
            </select>

            <button
              onClick={addTask}
              className="px-4 py-2 text-xs text-green-400 border border-green-400/60 rounded-full"
            >
              Add
            </button>

            <button
              onClick={toggleAdd}
              className="px-4 py-2 text-xs text-red-400 border border-red-400/60 rounded-full"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* List */}
      <div className="space-y-2">
        {tasks.map((task) => (
          <div
            key={task.id}
            className={`p-3 rounded bg-black/30 border border-white/5 flex items-center justify-between ${
              task.completed ? "opacity-60" : ""
            }`}
          >
            <div className="flex items-center gap-3 flex-1">
              <input
                type="checkbox"
                checked={task.completed}
                onChange={() => toggleCompletedLocal(task.id)}
              />

              {editingId === task.id ? (
                <div className="flex gap-2 flex-1">
                  <input
                    className="flex-1 bg-black/40 border border-white/10 px-2 py-1 rounded"
                    value={editDraft.text}
                    onChange={(e) =>
                      setEditDraft({ ...editDraft, text: e.target.value })
                    }
                    onKeyDown={(e) => handleEditKey(e, task.id)}
                    autoFocus
                  />

                  <select
                    className="px-2 py-1 text-xs bg-black/60 border border-white/20 rounded"
                    value={editDraft.priority}
                    onChange={(e) =>
                      setEditDraft({ ...editDraft, priority: e.target.value })
                    }
                  >
                    <option value="LOW">low</option>
                    <option value="MEDIUM">medium</option>
                    <option value="HIGH">high</option>
                  </select>
                </div>
              ) : (
                <div className="flex-1">
                  <div className="text-sm">{task.text}</div>
                  <div className="text-[11px] text-soft uppercase">
                    {task.priority}
                  </div>
                </div>
              )}
            </div>

            <div className="flex gap-2">
              <button onClick={() => startEdit(task)}>
                <Edit2 className="w-4 h-4" />
              </button>
              <button onClick={() => deleteTask(task.id)}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}